import { mdiBugOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { useTranslation } from 'react-i18next'

interface FeedbackButtonProps {
  onClick: () => void
  isCapturing?: boolean
  isRecording?: boolean
  /**
   * The desktop header instance and the mobile sidebar-footer instance are
   * both mounted at all times (only CSS hides one), so they must not share a
   * `data-testid` — Playwright's getByTestId would resolve to two elements.
   */
  testId?: string
}

// macOS uses ⌘, every other desktop platform uses Ctrl. Mobile UAs don't
// have keyboards so we just show the action label.
function isMac(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform)
}

export function FeedbackButton({
  onClick,
  isCapturing,
  isRecording,
  testId = 'feedback-trigger-btn',
}: FeedbackButtonProps) {
  const { t } = useTranslation('feedback')

  const shortcut = isMac() ? '⌘⇧B' : 'Ctrl+Shift+B'
  const label = isRecording ? t('stop_recording_hint') : t('trigger_title')
  const titleWithShortcut = isRecording ? label : `${label} (${shortcut})`

  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={isCapturing}
      className="relative p-2 hover:bg-accent rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      title={titleWithShortcut}
      aria-label={titleWithShortcut}
      aria-keyshortcuts="Control+Shift+B Meta+Shift+B"
    >
      <AppIcon path={mdiBugOutline} size="sm" />
      {isRecording && (
        <span className="absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />
      )}
    </button>
  )
}
